import { useEffect, useState } from 'react'
import type { ContactJson } from 'app/types/entities'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { MAX_SEARCH_CONTACTS, buildContactSearch, type InboxSearchTargets } from 'app/lib/inboxSearch'
import { phoneDigits } from 'lib/phone'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

/**
 * Resolution state for the inbox search box.
 *
 * `resolving` matters to the caller: the conversation list must not fall back to
 * an unfiltered fetch in the window between a keystroke and the contact lookup
 * landing, or the full list flashes back in mid-search.
 */
export type InboxSearchResolution =
  | { status: `idle` }
  | { status: `resolving` }
  | { status: `resolved`; targets: InboxSearchTargets }

/**
 * Resolve an inbox search query into the phone numbers whose threads should show.
 *
 * Names live on the contacts index and phone numbers on the messages index, so a
 * query that reads as one search to the user is two lookups here: contacts
 * first, then their numbers handed to the message search. See `app/lib/inboxSearch`.
 */
export function useInboxSearch(query: string, locationId: string): InboxSearchResolution {
  const trimmedQuery = query.trim()
  const [resolution, setResolution] = useState<InboxSearchResolution>({ status: `idle` })

  useEffect(() => {
    if (!trimmedQuery || !locationId) {
      setResolution({ status: `idle` })
      return
    }
    setResolution({ status: `resolving` })
    const digits = phoneDigits(trimmedQuery)
    const search = buildContactSearch(trimmedQuery)
    if (!search) {
      setResolution({ status: `resolved`, targets: { phones: [], digits } })
      return
    }
    // No debounce here on purpose: `query` arrives from the URL, and the inbox
    // search box already debounces that write (SEARCH_DEBOUNCE_MS in
    // InboxSubnavs), so this changes once per typing pause, not per keystroke.
    // A second debounce would only add latency.
    let cancelled = false
    bg(async () => {
      try {
        const { hits } = await gqlFetchList<ContactJson>(
          `contacts`,
          { search, pagination: { limit: MAX_SEARCH_CONTACTS } },
          `id name cellPhone`,
        )
        if (cancelled) return
        const phones = [...new Set(hits.map((hit) => phoneDigits(hit.cellPhone ?? ``)).filter(Boolean))]
        setResolution({ status: `resolved`, targets: { phones, digits } })
      } catch (err) {
        if (cancelled) return
        logError(`Error resolving inbox search contacts`, err)
        // Fall back to the digit match alone rather than stranding the user on a
        // spinner: a number query still works, a name query shows empty.
        setResolution({ status: `resolved`, targets: { phones: [], digits } })
      }
    })
    return () => {
      cancelled = true
    }
  }, [trimmedQuery, locationId])

  return resolution
}
